import { usePost } from "../api-client/usePost";
import { LeadFormData, LeadParams } from "../blocks/LeadForm/LeadFormModal";
import { useTireCompareIdsWithQuickCompareFallback } from "../blocks/Tires/TiresCompareDialog/useTireCompareIds";
import { partTypeTire } from "../constants/partTypes";
import { useLanguage } from "../i18n/useLanguage";
import { useCart } from "../stores/hooks/useCart";
import { useDealerAccountInfo } from "../stores/hooks/useDealerAccountInfo";
import { useSetInfoToast } from "../stores/hooks/useSetInfoToast";
import { useLogFeatureUsage } from "./useLogFeatureUsage";

type CreateQuoteContactLead = (params: LeadParams) => Promise<LeadFormData>;

export const useCreateComparisonLead = (): CreateQuoteContactLead => {
  // Both calls write (a lead row, then the comparison e-mail), so both are form POSTs;
  // the lead used to go as a GET with the customer's details in the URL (TW-1122).
  const { send: createLead } = usePost(
    "/retailSiteWebService/shopping/lead/createComparisonLead.php"
  );
  const { send } = usePost(
    "/retailSiteWebService/parts/comparison/emailComparison.php"
  );
  const cart = useCart();
  const [language] = useLanguage();
  const [partIds] = useTireCompareIdsWithQuickCompareFallback();
  const { dealerId } = useDealerAccountInfo();
  const setInfoToast = useSetInfoToast();
  const logFeatureUsage = useLogFeatureUsage();

  const createQuoteContactLead: CreateQuoteContactLead = async ({
    customerEmail,
    customerFirstName,
    customerLastName,
    customerMessage,
    customerPhone,
    customerVin,
    generatedBy,
  }): Promise<LeadFormData> => {
    const targetDealerId = cart?.dealerId || dealerId;
    const partIdsString = partIds.join(",");
    await createLead({
      customerEmail,
      customerFirstName,
      customerLanguage: language,
      customerLastName,
      customerMessage,
      customerPhone,
      customerVin,
      generatedBy,
      orderNumber: String(cart?.orderNumber),
      partIds: partIdsString,
      partType: partTypeTire,
      sendCustomerEmail: "false",
      targetDealerId,
    });
    await send({
      customerCurrentLanguage: language,
      customerEmail,
      customerFirstName,
      customerLastName,
      customerNotes: customerMessage,
      customerPhone,
      dealerId: targetDealerId,
      partId: partIdsString,
      partType: "TRE",
    });
    setInfoToast({ message: "leadForm.successMessage" });
    logFeatureUsage("emailComparison");

    return {
      customerEmail,
      customerFirstName,
      customerLastName,
      customerMessage,
      customerPhone,
      customerVin,
      generatedBy,
    };
  };

  return createQuoteContactLead;
};
